import { FlexBox, ParentTrainingStatus, SPACING } from "@joonapp/web-shared"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"

import CatchUpButton from "./CatchUpButton"
import CatchUpCompleteButton from "./CatchUpCompleteButton"
import CoachAlertSection from "./CoachAlertSection"
import DoterAIAlertsSection from "./DoterAIAlertsSection"
import DownloadAppBanner from "./DownloadAppBanner"
import InviteABASection from "./parentTasks/InviteABASection"
import ParentTasksSection from "./parentTasks/ParentTasksSection"
import QuickActionsSection from "./QuickActionsSection"
import ReferralFTUXModal from "./referralModal/ReferralFTUXModal"
import ReferralGiftButton from "./referralModal/ReferralGiftButton"
import ReferralGiftCardSection from "./referralModal/ReferralGiftCardSection"
import ReferralModal from "./referralModal/ReferralModal"
import { useShowReferralFTUXModal } from "./referralModal/useShowReferralFTUXModal"
import TodaysProgressSection from "./TodaysProgressSection"
import ChildAuthCode from "../../components/childAuthCode/ChildAuthCode"
import GetStartedSection from "../../components/getStarted/GetStartedSection"
import PageContentWrapper from "../../components/pageContentWrapper/PageContentWrapper"
import PageHeaderLeft from "../../components/pageHeader/PageHeaderLeft"
import PageWrapper from "../../components/pageWrapper/PageWrapper"
import { SkeletonLoader } from "../../components/skeleton/Skeleton"
import { GRADIENTS, QUERY_KEYS } from "../../constants"
import useDocumentTitle from "../../hooks/useDocumentTitle"
import {
  useFamilyQuery,
  useGetMeTabCardQuery,
  useTrainingsQuery,
  useUserQuery,
} from "../../networking/queries"
import { useCustomRewardsQuery } from "../../networking/rewards/queries"
import { FamilyQuerySelect } from "../../types"
import { requireAuth } from "../../util/auth"
import { useRouter } from "../../util/router"
import { localStorageItems } from "../../util/storage"
import { noOnboardedChildren } from "../../util/util"
import TrainingsList from "../academy/TrainingsList"
import useCareTeam from "../care/useCareTeam"
import ActiveScreenTimeSection from "../rewards/ActiveScreenTimeSection"

const MePage = requireAuth(() => {
  useDocumentTitle("Joon")
  const { t } = useTranslation()
  const { user } = useUserQuery()
  const { isLoading: isLoadingMeTabCard, isFetching: isFetchingMeTabCard } =
    useGetMeTabCardQuery()
  const { data: children, isLoading: isLoadingChildren } = useFamilyQuery(
    FamilyQuerySelect.CHILDREN
  )
  const {
    hasCoach,
    hasTherapist,
    firstTherapist,
    isLoading: isLoadingCareTeam,
  } = useCareTeam()
  const { data: trainingsIP, isLoading: isLoadingIP } = useTrainingsQuery(
    ParentTrainingStatus.IN_PROGRESS
  )
  useCustomRewardsQuery() // preload rewards for FTUX on rewards page
  const isLoading = isLoadingChildren || isLoadingCareTeam || isLoadingIP
  const queryClient = useQueryClient()
  const router = useRouter()
  const linkedAndNoOnboardedChildren =
    hasTherapist && noOnboardedChildren(children)
  const hasInProgressTrainings = trainingsIP && trainingsIP.length > 0

  useShowReferralFTUXModal() // show referral FTUX modal if the user has not seen it yet

  useEffect(() => {
    localStorage.removeItem(localStorageItems.parentQuestionnaireAnswers)
    queryClient.invalidateQueries([QUERY_KEYS.QUEST_BOARD])
    queryClient.invalidateQueries([QUERY_KEYS.QUEST_REVIEW])
    queryClient.invalidateQueries([QUERY_KEYS.QUEST_INSTANCE_STATS])
    if (hasCoach && !isLoadingMeTabCard && !isFetchingMeTabCard) {
      queryClient.invalidateQueries([QUERY_KEYS.ME_TAB_CARD])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (localStorage.getItem(localStorageItems.coachOnboardingComplete)) {
      localStorage.removeItem(localStorageItems.coachOnboardingComplete)
      router.push("/coach")
    }
    if (
      localStorage.getItem(localStorageItems.needsToSeeTherapistTour) &&
      firstTherapist
    ) {
      router.push(`/care/${firstTherapist.user.id}/academy`)
    }
  }, [router, firstTherapist])

  return (
    <PageWrapper style={{ background: GRADIENTS.gradient100 }}>
      <DownloadAppBanner />
      <PageContentWrapper mobileGutter>
        <PageHeaderLeft>
          <div className="flex flex-row gap-2 items-center justify-between relative z-10">
            {t("ME_WELCOME_BACK", { name: user?.name })}
            <ReferralGiftButton />
          </div>
        </PageHeaderLeft>
        {linkedAndNoOnboardedChildren || isLoading ? (
          <div style={{ minHeight: SPACING.space3 }} />
        ) : (
          <ChildAuthCode />
        )}
        <FlexBox direction="column" gap={SPACING.space4} align="unset">
          <ActiveScreenTimeSection />
          <CatchUpButton />
          <CatchUpCompleteButton />
          {!linkedAndNoOnboardedChildren &&
            (isLoading ? (
              <SkeletonLoader style={{ borderRadius: SPACING.space2 }} />
            ) : (
              <QuickActionsSection />
            ))}
          <GetStartedSection />
          <ReferralGiftCardSection />
          {hasCoach && <CoachAlertSection />}
          <DoterAIAlertsSection />
          <ParentTasksSection />
          <InviteABASection />
          {hasInProgressTrainings && <TrainingsList />}
          {!linkedAndNoOnboardedChildren &&
            (isLoading ? (
              <SkeletonLoader style={{ borderRadius: SPACING.space2 }} />
            ) : (
              <TodaysProgressSection />
            ))}
        </FlexBox>
        <ReferralFTUXModal />
        <ReferralModal />
      </PageContentWrapper>
    </PageWrapper>
  )
})

export default MePage
